import React, { useState } from "react";
import {
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  MessageCircle,
  Loader2,

} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { auth, db, googleProvider } from "../firebase/firebase";

function Login() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleChange = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });

    setError("");
    setSuccess("");
  };

  // --------------------------------------------------
  // Create user document if Google user does not have one
  // --------------------------------------------------

  const createGoogleUserDocument = async (user) => {
    const userRef = doc(db, "users", user.uid);

    const existingUser = await getDoc(userRef);

    if (existingUser.exists()) {
      return existingUser.data();
    }

    const referralCode = `REF-${user.uid.slice(0, 8).toUpperCase()}`;

    const userData = {
      uid: user.uid,
      name: user.displayName || "",
      email: user.email || "",
      phone: user.phoneNumber || "",

      role: "user",

      referralCode,
      referredBy: "",

      paymentStatus: "not_paid",
      softwareActivated: false,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, userData);

    return userData;
  };

  // --------------------------------------------------
  // Redirect according to user role
  // --------------------------------------------------

  const redirectUser = async (user) => {
    const userRef = doc(db, "users", user.uid);
    const userSnapshot = await getDoc(userRef);

    let userData;

    if (userSnapshot.exists()) {
      userData = userSnapshot.data();
    } else {
      // Google account may not have a Firestore document yet
      userData = await createGoogleUserDocument(user);
    }

    if (userData?.role === "admin") {
      navigate("/admin", { replace: true });
      return;
    }

    navigate("/dashboard", { replace: true });
  };

  // --------------------------------------------------
  // Email + Password Login
  // --------------------------------------------------

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const email = form.email.trim();
    const password = form.password;

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      await redirectUser(user);
    } catch (firebaseError) {
      console.error("Login error:", firebaseError);

      switch (firebaseError.code) {
        case "auth/invalid-credential":
          setError("Invalid email or password. Please try again.");
          break;

        case "auth/user-not-found":
          setError("No account found with this email address.");
          break;

        case "auth/wrong-password":
          setError("Incorrect password. Please try again.");
          break;

        case "auth/invalid-email":
          setError("Please enter a valid email address.");
          break;

        case "auth/user-disabled":
          setError(
            "This account has been disabled. Please contact Referx support."
          );
          break;

        case "auth/too-many-requests":
          setError(
            "Too many login attempts. Please wait a while and try again."
          );
          break;

        case "auth/network-request-failed":
          setError(
            "Network error. Please check your internet connection and try again."
          );
          break;

        default:
          setError(
            firebaseError.message || "Unable to login. Please try again."
          );
      }
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Google Login
  // --------------------------------------------------

  const handleGoogleLogin = async () => {
    setError("");
    setSuccess("");
    setGoogleLoading(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);

      const user = result.user;

      await redirectUser(user);
    } catch (firebaseError) {
      console.error("Google login error:", firebaseError);

      switch (firebaseError.code) {
        case "auth/popup-closed-by-user":
          setError("Google login was cancelled.");
          break;

        case "auth/popup-blocked":
          setError(
            "Google login popup was blocked. Please allow popups and try again."
          );
          break;

        case "auth/account-exists-with-different-credential":
          setError(
            "An account already exists with this email using another login method."
          );
          break;

        case "auth/network-request-failed":
          setError(
            "Network error. Please check your internet connection and try again."
          );
          break;

        default:
          setError(
            firebaseError.message ||
              "Google login failed. Please try again."
          );
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // --------------------------------------------------
  // Forgot Password
  // --------------------------------------------------

  const handleForgotPassword = async () => {
    setError("");
    setSuccess("");

    const email = form.email.trim();

    if (!email) {
      setError(
        "Please enter your email address first to reset your password."
      );
      return;
    }

    setResetLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);

      setSuccess(
        "Password reset email sent. Please check your inbox."
      );
    } catch (firebaseError) {
      console.error("Password reset error:", firebaseError);

      switch (firebaseError.code) {
        case "auth/invalid-email":
          setError("Please enter a valid email address.");
          break;

        case "auth/user-not-found":
          setError("No account found with this email address.");
          break;

        case "auth/network-request-failed":
          setError(
            "Network error. Please check your internet connection."
          );
          break;

        default:
          setError(
            firebaseError.message ||
              "Unable to send password reset email."
          );
      }
    } finally {
      setResetLoading(false);
    }
  };

  const isAnyLoading =
    loading || googleLoading || resetLoading;

  return (
    <main className="min-h-screen bg-[#f6f8fb] px-5 py-10 sm:py-16">
      <div className="mx-auto max-w-5xl">
        <div className="grid overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_25px_70px_rgba(15,23,42,0.08)] lg:grid-cols-2">

          {/* ==================================================
              LEFT PANEL
          ================================================== */}

          <div className="relative hidden overflow-hidden bg-[#07121f] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">

            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />

            <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />

            <div className="relative">

              {/* Logo */}

              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00a96d] shadow-lg shadow-emerald-500/20">
                  <MessageCircle size={22} />
                </div>

                <div>
                  <div className="text-lg font-black">
                    Ref<span className="text-emerald-400">rex</span>
                  </div>

                  <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">
                    WhatsApp Automation
                  </div>
                </div>
              </div>

              {/* Hero */}

              <div className="mt-20 max-w-md">

                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">
                  Welcome Back
                </div>

                <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.05em] xl:text-5xl">
                  Your Referx
                  <br />
                  Dashboard Awaits.
                </h1>

                <p className="mt-5 text-sm leading-7 text-slate-400">
                  Login to manage your software access, tasks, referrals
                  and wallet from one professional dashboard.
                </p>

              </div>
            </div>

            <div className="relative text-xs font-bold text-slate-500">
              WhatsApp Automation & Referral Engine
            </div>
          </div>

          {/* ==================================================
              FORM PANEL
          ================================================== */}

          <div className="p-6 sm:p-10 lg:p-12 xl:p-14">

            <div className="mx-auto max-w-md">

              {/* Mobile Logo */}

              <div className="flex justify-center lg:hidden">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00a96d] text-lg font-black text-white shadow-lg shadow-emerald-500/20">
                  R
                </div>
              </div>

              {/* Heading */}

              <div className="mt-5 text-center lg:mt-0 lg:text-left">

                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
                  Account Login
                </div>

                <h2 className="mt-2 text-3xl font-black tracking-[-0.05em] text-[#08111f]">
                  Welcome Back
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Login to continue to your Referx dashboard.
                </p>

              </div>

              {/* Error */}

              {error && (
                <div className="mt-6 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs font-bold leading-5 text-rose-600">
                  {error}
                </div>
              )}

              {/* Success */}

              {success && (
                <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs font-bold leading-5 text-emerald-700">
                  {success}
                </div>
              )}

              {/* ==================================================
                  LOGIN FORM
              ================================================== */}

              <form
                onSubmit={handleSubmit}
                className="mt-8 space-y-5"
              >

                {/* Email */}

                <div>

                  <label className="mb-2 block text-xs font-black text-slate-700">
                    Email Address
                  </label>

                  <div className="relative">

                    <Mail
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      required
                      disabled={isAnyLoading}
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm font-medium outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/5 disabled:cursor-not-allowed disabled:opacity-60"
                    />

                  </div>

                </div>

                {/* Password */}

                <div>

                  <div className="mb-2 flex items-center justify-between">

                    <label className="text-xs font-black text-slate-700">
                      Password
                    </label>

                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isAnyLoading}
                      className="flex items-center gap-1 text-[10px] font-black text-[#00a96d] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {resetLoading && (
                        <Loader2
                          size={11}
                          className="animate-spin"
                        />
                      )}

                      {resetLoading
                        ? "Sending..."
                        : "Forgot Password?"}
                    </button>

                  </div>

                  <div className="relative">

                    <LockKeyhole
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      required
                      disabled={isAnyLoading}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-11 text-sm font-medium outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/5 disabled:cursor-not-allowed disabled:opacity-60"
                    />

                    <button
                      type="button"
                      disabled={isAnyLoading}
                      onClick={() =>
                        setShowPassword(!showPassword)
                      }
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700 disabled:opacity-50"
                    >
                      {showPassword ? (
                        <EyeOff size={17} />
                      ) : (
                        <Eye size={17} />
                      )}
                    </button>

                  </div>

                </div>

                {/* Login Button */}

                <button
                  type="submit"
                  disabled={isAnyLoading}
                  className="group flex h-12 w-full items-center justify-center rounded-xl bg-[#00a96d] text-sm font-black text-white shadow-lg shadow-emerald-500/15 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#00945f] disabled:cursor-not-allowed disabled:opacity-60"
                >

                  {loading ? (
                    <>
                      <Loader2
                        size={17}
                        className="mr-2 animate-spin"
                      />
                      Signing In...
                    </>
                  ) : (
                    <>
                      Login

                      <ArrowRight
                        size={16}
                        className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </>
                  )}

                </button>

              </form>

              {/* ==================================================
                  DIVIDER
              ================================================== */}

              <div className="my-7 flex items-center gap-3">

                <div className="h-px flex-1 bg-slate-100" />

                <span className="text-[10px] font-bold text-slate-400">
                  OR CONTINUE WITH
                </span>

                <div className="h-px flex-1 bg-slate-100" />

              </div>

              {/* ==================================================
                  GOOGLE LOGIN
              ================================================== */}

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isAnyLoading}
                className="flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white text-sm font-black text-slate-700 transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >

                {googleLoading ? (
                  <Loader2
                    size={17}
                    className="animate-spin"
                  />
                ) : (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-xs font-black">
                    G
                  </span>
                )}

                {googleLoading
                  ? "Connecting..."
                  : "Continue with Google"}

              </button>

              {/* ==================================================
                  CREATE ACCOUNT
              ================================================== */}

              <div className="my-8 flex items-center gap-3">

                <div className="h-px flex-1 bg-slate-100" />

                <span className="text-[10px] font-bold text-slate-400">
                  NEW TO REFERX?
                </span>

                <div className="h-px flex-1 bg-slate-100" />

              </div>

              <Link
                to="/signup"
                className="flex h-12 w-full items-center justify-center rounded-xl border border-slate-200 text-sm font-black text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-[#00a96d]"
              >
                Create New Account
              </Link>

              {/* Footer */}

              <p className="mt-7 text-center text-[10px] leading-5 text-slate-400">
                Transparent performance-based earnings. Strictly ₹200 per
                verified software purchase.
              </p>

            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Login;